import { CustomLink } from '@/components/commons/typography/CustomLinkt'
import { Text } from '@/components/commons/typography/Text'
import { LoadingText } from '@/components/skeleton/TextSkeleton'
import { LocationIcon, WebsiteIcon } from '@/public/assets/v2/images/icons'
import {
  useIsOwnProfile,
  useProfile,
} from 'containers/profile/hooks/useProfile'
import { BrowserContext } from 'context'
import { useContext, useMemo } from 'react'
import { APP_LINKS, joinWithSlash } from 'utilities/links'
import { trimText } from 'utilities/text'
import { useRouter } from 'next/router'
import { useUser } from 'containers/user/hooks/useUser'
import { getLinkSafeUrl } from '@/shared/utils'

export const LocationAndWebsite = () => {
  const { profile, isLoading } = useProfile()
  const { isAuth } = useUser()
  const ownProfile = useIsOwnProfile()
  const router = useRouter()
  const { isMobile } = useContext(BrowserContext)

  const canEdit = useMemo(() => isAuth && ownProfile, [isAuth, ownProfile])

  const textLength = useMemo(() => (isMobile ? 12 : 30), [isMobile])

  const locationLabel = useMemo(() => {
    const location = profile?.location

    if (ownProfile && !location) {
      return 'Add location'
    }

    return location
  }, [ownProfile, profile?.location])

  const websiteLabel = useMemo(() => {
    const url = profile?.externalLink?.url
    const linkText = profile?.externalLink?.linkText

    // only show edit copy to owner
    // can't show a link without a url
    if (ownProfile && !url) {
      return 'Add External Link'
    }

    // show link text if it exists as a priority
    if (url && linkText) {
      return linkText
    }

    // fallback to url
    if (url) {
      return url
    }
  }, [ownProfile, profile?.externalLink?.linkText, profile?.externalLink?.url])

  const maxTextLength = useMemo(() => {
    // TODO: joxxe add logic for desktop
    const locationLength = locationLabel?.length || 0
    const websiteLength = websiteLabel?.length || 0

    let maxLocationLength = textLength
    let maxWebsiteLength = textLength

    if (websiteLength < textLength) {
      maxLocationLength = textLength + (textLength - websiteLength)
    }

    if (locationLength < textLength) {
      maxWebsiteLength = textLength + (textLength - locationLength)
    }

    return {
      location: maxLocationLength,
      website: maxWebsiteLength,
    }
  }, [locationLabel?.length, textLength, websiteLabel?.length])

  return (
    <div className="flex gap-16 items-center full-w">
      {(isLoading || locationLabel) && (
        <div className="flex gap-8 items-center">
          <div>
            <LocationIcon
              style={{ width: 16, height: 16, marginTop: 4 }}
              className="text-tertirary"
            />
          </div>
          {LoadingText(
            <Text
              sizeClass="text-12"
              colorClass="text-tertirary"
              className={canEdit && !profile?.location && 'cursor-pointer'}
              onClick={() =>
                canEdit &&
                !profile?.location &&
                router.push(
                  joinWithSlash(
                    profile?.username.toLocaleLowerCase(),
                    APP_LINKS.PROFILE_EDIT
                  )
                )
              }
            >
              {trimText(locationLabel, maxTextLength.location)}
            </Text>,
            isLoading,
            80,
            14
          )}
        </div>
      )}
      {(isLoading || websiteLabel) && (
        <div className="flex gap-8 items-center">
          <div>
            <WebsiteIcon
              style={{ width: 16, height: 16, marginTop: 6 }}
              className={
                profile?.externalLink?.url ? 'text-info' : 'text-tertirary'
              }
            />
          </div>
          {LoadingText(
            <CustomLink
              href={
                profile?.externalLink?.url
                  ? getLinkSafeUrl(profile?.externalLink?.url)
                  : '#'
              }
              target={profile?.externalLink?.url ? '_blank' : '_self'}
              sizeClass="text-12"
              onClick={() =>
                canEdit &&
                !profile?.externalLink?.url &&
                router.push(
                  joinWithSlash(
                    profile?.username.toLocaleLowerCase(),
                    APP_LINKS.PROFILE_EDIT
                  )
                )
              }
              colorClass={
                profile?.externalLink?.url ? 'text-info' : 'text-tertirary'
              }
            >
              {trimText(websiteLabel, maxTextLength.website)}
            </CustomLink>,
            isLoading,
            80,
            14
          )}
        </div>
      )}
    </div>
  )
}
